import { createEffect, createMemo, createSignal, For, on, Show, untrack } from "solid-js";
import { Portal } from "solid-js/web";
import { Icon } from "~/icon";
import { clientReload, getSearchParams, router, usePage } from "~/inertia";
import { formatDate } from "~/util/format";
import { parseDateInTimezone, todayInTimezone } from "~/util/date";
import { createFloating } from "~/util/html/floating";
import clsx from "~/util/html/style";
import { hidePopover, showPopover } from "~/util/html/top_layer";
import { FilterButton, isRowLink } from "./filter";
import { Tooltip } from "./tooltip";

/** Reload with new params without mangling existing array params like locations[]. */
function reloadWithMergedParams(data: Record<string, string>, only?: string[]) {
  const params = new URLSearchParams(window.location.search);
  for (const key of Object.keys(data)) params.delete(key);
  for (const [key, value] of Object.entries(data)) params.set(key, value);
  router.visit(`${window.location.pathname}?${params}`, {
    method: "get",
    replace: true,
    preserveState: true,
    preserveScroll: true,
    ...(only ? { only } : {}),
  });
}

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export type CalendarDate = { year: number; month: number; day: number };
type DateRange = { start: string; end: string };

export type DateRangePreset = {
  label: string;
  getRange: () => DateRange;
};

function toCalendarDate(date: Date): CalendarDate {
  return { year: date.getFullYear(), month: date.getMonth(), day: date.getDate() };
}

// Nullable on purpose: callers that take a date from a prop or the network cannot assume it
// parsed. Building the fields by hand rather than via `new Date` is what keeps this
// timezone-safe — `new Date("2026-08-03")` is UTC midnight, i.e. the 2nd west of Greenwich.
export function tryParseISO(iso: string): CalendarDate | null {
  const [year, month, day] = iso.split("-").map(Number);
  if (year === undefined || month === undefined || day === undefined) return null;
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
  return { year, month: month - 1, day };
}

const UNPARSEABLE: CalendarDate = { year: NaN, month: NaN, day: NaN };

function parseISO(iso: string): CalendarDate {
  return tryParseISO(iso) ?? UNPARSEABLE;
}

export function toISO(cal: CalendarDate): string {
  return `${cal.year}-${String(cal.month + 1).padStart(2, "0")}-${String(cal.day).padStart(2, "0")}`;
}

function addMonthsISO(iso: string, months: number): string {
  const from = parseISO(iso);
  const target = new Date(from.year, from.month + months, 1);
  const year = target.getFullYear();
  const month = target.getMonth();
  return toISO({ year, month, day: Math.min(from.day, getDaysInMonth(year, month)) });
}

export function clampRangeStart(start: string, end: string, maxMonths: number): string {
  const floor = addMonthsISO(end, -maxMonths);
  return start < floor ? floor : start;
}

function isSameDay(a: CalendarDate, b: CalendarDate): boolean {
  return a.year === b.year && a.month === b.month && a.day === b.day;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

export function daysAgo(days: number, today?: Date): Date {
  const date = new Date(today ?? new Date());
  date.setDate(date.getDate() - days);
  return date;
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function startOfYear(date: Date): Date {
  return new Date(date.getFullYear(), 0, 1);
}

function endOfYear(date: Date): Date {
  return new Date(date.getFullYear(), 11, 31);
}

function getLastQuarter(today?: Date): { start: Date; end: Date } {
  const now = today ?? new Date();
  const currentQuarter = Math.floor(now.getMonth() / 3);
  const lastQuarterStart = currentQuarter === 0 ? 9 : (currentQuarter - 1) * 3;
  const year = currentQuarter === 0 ? now.getFullYear() - 1 : now.getFullYear();
  return {
    start: new Date(year, lastQuarterStart, 1),
    end: endOfMonth(new Date(year, lastQuarterStart + 2, 1)),
  };
}

export function dateToISO(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function getPrevMonth(year: number, month: number): { year: number; month: number } {
  if (month === 0) return { year: year - 1, month: 11 };
  return { year, month: month - 1 };
}

function getNextMonth(year: number, month: number): { year: number; month: number } {
  if (month === 11) return { year: year + 1, month: 0 };
  return { year, month: month + 1 };
}

function findMatchingPreset(
  startValue: string | undefined,
  endValue: string | undefined,
  presets: DateRangePreset[],
): string | undefined {
  if (!startValue || !endValue) return undefined;
  for (const preset of presets) {
    const range = preset.getRange();
    if (range.start === startValue && range.end === endValue) {
      return preset.label;
    }
  }
  return undefined;
}

export function todayISO(timezone?: string): string {
  return dateToISO(nowInTimezone(timezone));
}

/**
 * The day a preset resolves against, as a local midnight Date.
 *
 * The reference is an INSTANT, not a day: 03:00 UTC is already the 7th in
 * Auckland and still the 6th in Los Angeles. So a reference given with a
 * timezone is re-read as that zone's calendar day, or a viewer abroad seeds a
 * pinned report's range off their own midnight and the picker beside it —
 * which resolves the same preset in the account's zone — disagrees.
 */
function nowInTimezone(timezone?: string, reference?: Date): Date {
  if (!timezone) return reference ?? new Date();
  const iso = reference
    ? parseDateInTimezone(reference.toISOString(), timezone)
    : todayInTimezone(timezone);
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y!, m! - 1, d);
}

/**
 * The nine rolling presets, resolved at getRange() time.
 *
 * `reference` pins the day they resolve against — a report resolving a stored
 * preset for a given day, or a test. Omit it and they resolve against the
 * clock. Either way the day is read in `timezone` when one is given.
 */
export function createDefaultPresets(timezone?: string, reference?: Date): DateRangePreset[] {
  const now = () => nowInTimezone(timezone, reference);
  return [
    {
      label: "Last 7 days",
      getRange: () => ({
        start: dateToISO(daysAgo(6, now())),
        end: dateToISO(now()),
      }),
    },
    {
      label: "Last 30 days",
      getRange: () => ({
        start: dateToISO(daysAgo(29, now())),
        end: dateToISO(now()),
      }),
    },
    {
      label: "Month to date",
      getRange: () => ({
        start: dateToISO(startOfMonth(now())),
        end: dateToISO(now()),
      }),
    },
    {
      label: "Last month",
      getRange: () => {
        const n = now();
        const lastMonth = new Date(n.getFullYear(), n.getMonth() - 1, 1);
        return {
          start: dateToISO(startOfMonth(lastMonth)),
          end: dateToISO(endOfMonth(lastMonth)),
        };
      },
    },
    {
      label: "Last quarter",
      getRange: () => {
        const q = getLastQuarter(now());
        return {
          start: dateToISO(q.start),
          end: dateToISO(q.end),
        };
      },
    },
    {
      label: "Last 6 months",
      getRange: () => {
        const n = now();
        const sixMonthsAgo = new Date(n.getFullYear(), n.getMonth() - 6, n.getDate());
        return {
          start: dateToISO(sixMonthsAgo),
          end: dateToISO(n),
        };
      },
    },
    {
      label: "Last 12 months",
      getRange: () => {
        const n = now();
        const twelveMonthsAgo = new Date(n.getFullYear(), n.getMonth() - 12, n.getDate());
        return {
          start: dateToISO(twelveMonthsAgo),
          end: dateToISO(n),
        };
      },
    },
    {
      label: "Year to date",
      getRange: () => ({
        start: dateToISO(startOfYear(now())),
        end: dateToISO(now()),
      }),
    },
    {
      label: "Last year",
      getRange: () => {
        const lastYear = now().getFullYear() - 1;
        return {
          start: dateToISO(startOfYear(new Date(lastYear, 0, 1))),
          end: dateToISO(endOfYear(new Date(lastYear, 0, 1))),
        };
      },
    },
  ];
}

export const DEFAULT_PRESETS: DateRangePreset[] = createDefaultPresets();

export function CalendarMonth(props: {
  year: number;
  month: number;
  onSelectDate: (date: CalendarDate) => void;
  onSelectMonth: (month: number) => void;
  onSelectYear: (year: number) => void;
  onPrev?: () => void;
  onNext?: () => void;
  isInRange?: (date: CalendarDate) => boolean;
  isRangeStart?: (date: CalendarDate) => boolean;
  isRangeEnd?: (date: CalendarDate) => boolean;
  isMarked?: (date: CalendarDate) => boolean;
  isDateDisabled?: (date: CalendarDate) => boolean;
  disabledReason?: string;
  minYear?: number;
  maxYear?: number;
  minMonth?: number;
  maxMonth?: number;
}) {
  const today = toCalendarDate(new Date());
  const [view, setView] = createSignal<"days" | "months" | "years">("days");

  const calendarDays = createMemo(() => {
    const daysInMonth = getDaysInMonth(props.year, props.month);
    const firstDay = getFirstDayOfMonth(props.year, props.month);

    const days: (CalendarDate | null)[] = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({ year: props.year, month: props.month, day });
    }
    return days;
  });

  const isMonthDisabled = (monthIndex: number) => {
    if (props.minYear !== undefined && props.year < props.minYear) return true;
    if (props.maxYear !== undefined && props.year > props.maxYear) return true;
    if (props.year === props.minYear && props.minMonth !== undefined && monthIndex < props.minMonth)
      return true;
    if (props.year === props.maxYear && props.maxMonth !== undefined && monthIndex > props.maxMonth)
      return true;
    return false;
  };

  const isYearDisabled = (year: number) => {
    if (props.minYear !== undefined && year < props.minYear) return true;
    if (props.maxYear !== undefined && year > props.maxYear) return true;
    return false;
  };

  const yearRange = createMemo(() => {
    const startYear = props.year - 5;
    const years: number[] = [];
    for (let i = 0; i < 12; i++) {
      years.push(startYear + i);
    }
    return years;
  });

  return (
    <div>
      <div class="relative mb-1 flex items-center justify-between px-1">
        <button
          type="button"
          onClick={() => props.onPrev?.()}
          class={clsx(
            "flex h-8 w-5 items-center justify-center text-typography-secondary",
            props.onPrev ? "opacity-50 hover:opacity-100" : "cursor-default opacity-20",
          )}
        >
          <Icon name="chevronLeft" size="sm" />
        </button>

        <div class="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setView(view() === "months" ? "days" : "months")}
            class={clsx(
              "rounded px-2 py-1 text-sm leading-5 font-bold hover:bg-brand-hover",
              view() === "months" && "bg-brand-light text-brand-primary",
            )}
          >
            {MONTHS[props.month]}
          </button>
          <button
            type="button"
            onClick={() => setView(view() === "years" ? "days" : "years")}
            class={clsx(
              "rounded px-2 py-1 text-sm leading-5 font-bold hover:bg-brand-hover",
              view() === "years" && "bg-brand-light text-brand-primary",
            )}
          >
            {props.year}
          </button>
        </div>

        <button
          type="button"
          onClick={() => props.onNext?.()}
          class={clsx(
            "flex h-8 w-5 items-center justify-center text-typography-secondary",
            props.onNext ? "opacity-50 hover:opacity-100" : "cursor-default opacity-20",
          )}
        >
          <Icon name="chevronRight" size="sm" />
        </button>
      </div>

      <Show when={view() === "days"}>
        <div class="grid grid-cols-7">
          <For each={DAYS}>
            {(day) => (
              <div class="p-1 text-center text-xs leading-6 font-bold text-typography-secondary">
                {day}
              </div>
            )}
          </For>
        </div>

        <div class="grid grid-cols-7">
          <For each={calendarDays()}>
            {(date) => (
              <div class="p-1">
                <Show when={date} fallback={<div class="aspect-square" />}>
                  {(d) => {
                    const inRange = () => props.isInRange?.(d()) ?? false;
                    const isStart = () => props.isRangeStart?.(d()) ?? false;
                    const isEnd = () => props.isRangeEnd?.(d()) ?? false;
                    const isToday = () => isSameDay(d(), today);
                    const marked = () => props.isMarked?.(d()) ?? false;
                    const disabled = () => props.isDateDisabled?.(d()) ?? false;

                    return (
                      <Tooltip enabled={disabled()} content={props.disabledReason}>
                        {(triggerProps) => (
                          <button
                            type="button"
                            aria-disabled={disabled() || undefined}
                            onClick={() => {
                              if (!disabled()) props.onSelectDate(d());
                            }}
                            {...triggerProps}
                            class={clsx(
                              "block aspect-square w-full cursor-pointer text-center text-sm",
                              disabled() && "cursor-not-allowed text-typography-tertiary",
                              inRange() && !isStart() && !isEnd() && "bg-brand-light",
                              (isStart() || isEnd()) &&
                                "rounded-sm bg-brand-active font-bold text-white",
                              !isStart() &&
                                !isEnd() &&
                                isToday() &&
                                (marked() ? "font-bold" : "font-bold text-brand-primary"),
                              !inRange() &&
                                !isStart() &&
                                !isEnd() &&
                                !isToday() &&
                                !marked() &&
                                !disabled() &&
                                "text-typography-primary",
                              !isStart() &&
                                !isEnd() &&
                                !disabled() &&
                                "hover:rounded-sm hover:bg-brand-active hover:text-white",
                              marked() && !isStart() && !isEnd() && "text-typography-tertiary",
                            )}
                          >
                            {d().day}
                          </button>
                        )}
                      </Tooltip>
                    );
                  }}
                </Show>
              </div>
            )}
          </For>
        </div>
      </Show>

      <Show when={view() === "months"}>
        <div class="grid grid-cols-3 gap-1 p-1">
          <For each={MONTHS}>
            {(monthName, index) => {
              const disabled = () => isMonthDisabled(index());
              const isSelected = () => props.month === index();
              return (
                <button
                  type="button"
                  disabled={disabled()}
                  onClick={() => {
                    if (!disabled()) {
                      props.onSelectMonth(index());
                      setView("days");
                    }
                  }}
                  class={clsx(
                    "rounded px-2 py-2 text-sm",
                    disabled() && "text-typography-disabled cursor-not-allowed",
                    !disabled() && isSelected() && "bg-brand-active font-bold text-white",
                    !disabled() && !isSelected() && "text-typography-primary hover:bg-brand-hover",
                  )}
                >
                  {monthName.slice(0, 3)}
                </button>
              );
            }}
          </For>
        </div>
      </Show>

      <Show when={view() === "years"}>
        <div class="grid grid-cols-3 gap-1 p-1">
          <For each={yearRange()}>
            {(year) => {
              const disabled = () => isYearDisabled(year);
              const isSelected = () => props.year === year;
              return (
                <button
                  type="button"
                  disabled={disabled()}
                  onClick={() => {
                    if (!disabled()) {
                      props.onSelectYear(year);
                      setView("days");
                    }
                  }}
                  class={clsx(
                    "rounded px-2 py-2 text-sm",
                    disabled() && "text-typography-disabled cursor-not-allowed",
                    !disabled() && isSelected() && "bg-brand-active font-bold text-white",
                    !disabled() && !isSelected() && "text-typography-primary hover:bg-brand-hover",
                  )}
                >
                  {year}
                </button>
              );
            }}
          </For>
        </div>
      </Show>
    </div>
  );
}

export function DateRangePickerWithPresets(props: {
  startValue: string | undefined;
  endValue: string | undefined;
  onChange: (range: DateRange) => void;
  presets?: DateRangePreset[];
  timezone?: string;
  /** Idle-state button label when no range is selected (default "Select dates"). */
  placeholder?: string;
  /** Widest span a user can pick; days outside it are disabled once a start is chosen. */
  maxRangeMonths?: number;
}) {
  const [isOpen, setIsOpen] = createSignal(false);
  const [selecting, setSelecting] = createSignal<"start" | "end">("start");
  const [pendingStart, setPendingStart] = createSignal<string | undefined>(undefined);

  let triggerRef!: HTMLButtonElement;
  let popoverRef!: HTMLDivElement;
  let cleanup: (() => void) | null = null;

  const presets = () => props.presets ?? DEFAULT_PRESETS;
  const matchingPreset = createMemo(() =>
    findMatchingPreset(props.startValue, props.endValue, presets()),
  );

  const today = createMemo(() => toCalendarDate(nowInTimezone(props.timezone)));
  const initialDate = untrack(() => {
    if (props.endValue) return parseISO(props.endValue);
    if (props.startValue) return parseISO(props.startValue);
    return today();
  });

  const leftInit = getPrevMonth(initialDate.year, initialDate.month);
  const [leftYear, setLeftYear] = createSignal(leftInit.year);
  const [leftMonth, setLeftMonth] = createSignal(leftInit.month);
  const [rightYear, setRightYear] = createSignal(initialDate.year);
  const [rightMonth, setRightMonth] = createSignal(initialDate.month);

  const startDate = createMemo(() => (props.startValue ? parseISO(props.startValue) : undefined));
  const endDate = createMemo(() => (props.endValue ? parseISO(props.endValue) : undefined));

  const dateRangeDisplay = createMemo(() => {
    if (props.startValue && props.endValue) {
      return `${formatDate(props.startValue)} – ${formatDate(props.endValue)}`;
    }
    if (props.startValue) return formatDate(props.startValue);
    return undefined;
  });

  const handleOpen = () => {
    const focusDate = props.endValue
      ? parseISO(props.endValue)
      : props.startValue
        ? parseISO(props.startValue)
        : today();
    const leftDate = getPrevMonth(focusDate.year, focusDate.month);
    setLeftYear(leftDate.year);
    setLeftMonth(leftDate.month);
    setRightYear(focusDate.year);
    setRightMonth(focusDate.month);

    setIsOpen(true);
    // Without the Popover API the panel is a fixed element in body, shown by dropping `hidden`.
    showPopover(popoverRef);
    cleanup = createFloating({
      reference: triggerRef,
      floating: popoverRef,
      config: { placement: "bottom-start" },
    });
  };

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node;
    if (!triggerRef.contains(target) && !popoverRef.contains(target)) {
      handleClose();
      if (isRowLink(target)) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    hidePopover(popoverRef);
    setSelecting("start");
    setPendingStart(undefined);
    document.removeEventListener("click", handleClickOutside, true);
    if (cleanup) {
      cleanup();
      cleanup = null;
    }
  };

  const canLeftGoNext = () => {
    const next = getNextMonth(leftYear(), leftMonth());
    return next.year < rightYear() || (next.year === rightYear() && next.month < rightMonth());
  };

  const canRightGoPrev = () => {
    const prev = getPrevMonth(rightYear(), rightMonth());
    return prev.year > leftYear() || (prev.year === leftYear() && prev.month > leftMonth());
  };

  const prevLeftMonth = () => {
    const prev = getPrevMonth(leftYear(), leftMonth());
    setLeftYear(prev.year);
    setLeftMonth(prev.month);
  };

  const nextLeftMonth = () => {
    if (!canLeftGoNext()) return;
    const next = getNextMonth(leftYear(), leftMonth());
    setLeftYear(next.year);
    setLeftMonth(next.month);
  };

  const prevRightMonth = () => {
    if (!canRightGoPrev()) return;
    const prev = getPrevMonth(rightYear(), rightMonth());
    setRightYear(prev.year);
    setRightMonth(prev.month);
  };

  const nextRightMonth = () => {
    const next = getNextMonth(rightYear(), rightMonth());
    setRightYear(next.year);
    setRightMonth(next.month);
  };

  const selectDate = (date: CalendarDate) => {
    const iso = toISO(date);
    if (selecting() === "start") {
      setPendingStart(iso);
      setSelecting("end");
    } else {
      const start = pendingStart() ?? props.startValue ?? iso;
      if (iso < start) {
        props.onChange({ start: iso, end: start });
      } else {
        props.onChange({ start, end: iso });
      }
      handleClose();
      setPendingStart(undefined);
      setSelecting("start");
    }
  };

  const selectPreset = (preset: DateRangePreset) => {
    props.onChange(preset.getRange());
    handleClose();
  };

  const isInRange = (date: CalendarDate): boolean => {
    const pending = pendingStart();
    const start = pending ? parseISO(pending) : startDate();
    const end = pending ? undefined : endDate();
    if (!start || !end) return false;
    const iso = toISO(date);
    return iso > toISO(start) && iso < toISO(end);
  };

  const isRangeStart = (date: CalendarDate): boolean => {
    const pending = pendingStart();
    if (pending) return isSameDay(date, parseISO(pending));
    const start = startDate();
    return start ? isSameDay(date, start) : false;
  };

  const isRangeEnd = (date: CalendarDate): boolean => {
    if (pendingStart()) return false;
    const end = endDate();
    return end ? isSameDay(date, end) : false;
  };

  const isDateDisabled = (date: CalendarDate): boolean => {
    const pending = pendingStart();
    if (props.maxRangeMonths === undefined || !pending) return false;
    const iso = toISO(date);
    return (
      iso > addMonthsISO(pending, props.maxRangeMonths) ||
      iso < addMonthsISO(pending, -props.maxRangeMonths)
    );
  };

  return (
    <>
      {/* Fit, not the filter pills' usual cap: a range is always two short dates, and capped
          they pushed the caret past the pill's edge. */}
      <FilterButton
        ref={triggerRef}
        width="fit"
        onClick={() => {
          if (isOpen()) {
            handleClose();
          } else {
            handleOpen();
            document.addEventListener("click", handleClickOutside, true);
          }
        }}
      >
        <Icon name="calendar" size="sm" class="text-typography-secondary" />
        <Show
          when={matchingPreset()}
          fallback={
            <span class="text-sm text-typography-primary">
              {dateRangeDisplay() ?? props.placeholder ?? "Select dates"}
            </span>
          }
        >
          {(presetName) => (
            <div class="flex flex-col items-start py-1">
              <span class="text-sm leading-4 font-medium text-typography-primary">
                {presetName()}
              </span>
              <span class="text-xs leading-3 text-typography-secondary">{dateRangeDisplay()}</span>
            </div>
          )}
        </Show>
        <Icon
          name="chevronDown"
          size="sm"
          class="ml-auto text-typography-secondary group-hover/date-filter:text-typography-primary"
        />
      </FilterButton>

      <Portal>
        <div
          ref={popoverRef}
          popover="manual"
          class={clsx(
            "fixed z-floating-attachment mt-2 flex overflow-hidden rounded-xl bg-white shadow-md",
            !isOpen() && "hidden",
          )}
        >
          <div class="border-r border-brand-divider py-2">
            <For each={presets()}>
              {(preset) => {
                const isActive = () => matchingPreset() === preset.label;
                return (
                  <button
                    type="button"
                    onClick={() => selectPreset(preset)}
                    class={clsx(
                      "block w-full px-4 py-1.5 text-left text-sm",
                      isActive()
                        ? "bg-brand-light font-medium text-brand-primary"
                        : "text-typography-primary hover:bg-brand-hover",
                    )}
                  >
                    {preset.label}
                  </button>
                );
              }}
            </For>
          </div>

          <div class="flex gap-6 p-6">
            <CalendarMonth
              year={leftYear()}
              month={leftMonth()}
              onSelectDate={selectDate}
              isDateDisabled={isDateDisabled}
              disabledReason={`Range too large (up to ${props.maxRangeMonths} months)`}
              onSelectMonth={(month) => {
                setLeftMonth(month);
                if (
                  leftYear() > rightYear() ||
                  (leftYear() === rightYear() && month >= rightMonth())
                ) {
                  const next = getNextMonth(leftYear(), month);
                  setRightYear(next.year);
                  setRightMonth(next.month);
                }
              }}
              onSelectYear={(year) => {
                setLeftYear(year);
                if (year > rightYear() || (year === rightYear() && leftMonth() >= rightMonth())) {
                  const next = getNextMonth(year, leftMonth());
                  setRightYear(next.year);
                  setRightMonth(next.month);
                }
              }}
              onPrev={prevLeftMonth}
              onNext={canLeftGoNext() ? nextLeftMonth : undefined}
              isInRange={isInRange}
              isRangeStart={isRangeStart}
              isRangeEnd={isRangeEnd}
              maxYear={rightMonth() === 0 ? rightYear() - 1 : rightYear()}
              maxMonth={rightMonth() === 0 ? 11 : rightMonth() - 1}
            />
            <CalendarMonth
              year={rightYear()}
              month={rightMonth()}
              onSelectDate={selectDate}
              isDateDisabled={isDateDisabled}
              disabledReason={`Range too large (up to ${props.maxRangeMonths} months)`}
              onSelectMonth={(month) => {
                setRightMonth(month);
                if (
                  rightYear() < leftYear() ||
                  (rightYear() === leftYear() && month <= leftMonth())
                ) {
                  const prev = getPrevMonth(rightYear(), month);
                  setLeftYear(prev.year);
                  setLeftMonth(prev.month);
                }
              }}
              onSelectYear={(year) => {
                setRightYear(year);
                if (year < leftYear() || (year === leftYear() && rightMonth() <= leftMonth())) {
                  const prev = getPrevMonth(year, rightMonth());
                  setLeftYear(prev.year);
                  setLeftMonth(prev.month);
                }
              }}
              onPrev={canRightGoPrev() ? prevRightMonth : undefined}
              onNext={nextRightMonth}
              isInRange={isInRange}
              isRangeStart={isRangeStart}
              isRangeEnd={isRangeEnd}
              minYear={leftMonth() === 11 ? leftYear() + 1 : leftYear()}
              minMonth={leftMonth() === 11 ? 0 : leftMonth() + 1}
            />
          </div>
        </div>
      </Portal>
    </>
  );
}

export function QueryParamDateRangeQuickPresets(props: {
  startQueryParam: string;
  endQueryParam: string;
  presets: DateRangePreset[];
  reloadProp?: string | string[] | false;
  timezone?: string;
}) {
  const page = usePage();
  const urlStart = () => getSearchParams(page).get(props.startQueryParam);
  const urlEnd = () => getSearchParams(page).get(props.endQueryParam);

  const activeLabel = createMemo(() =>
    findMatchingPreset(urlStart() ?? undefined, urlEnd() ?? undefined, props.presets),
  );

  return (
    <div class="inline-flex items-center gap-1 rounded-full border border-brand-border bg-white p-0.5">
      <For each={props.presets}>
        {(preset) => {
          const isActive = () => activeLabel() === preset.label;
          return (
            <button
              type="button"
              class={clsx(
                "rounded-full px-3 py-1 text-sm font-medium transition-colors",
                isActive()
                  ? "bg-brand-primary text-white"
                  : "text-typography-secondary hover:text-typography-primary",
              )}
              onClick={() => {
                const range = preset.getRange();
                const data = {
                  [props.startQueryParam]: range.start,
                  [props.endQueryParam]: range.end,
                };

                if (props.reloadProp === false) {
                  clientReload({ data });
                } else {
                  const only = Array.isArray(props.reloadProp)
                    ? props.reloadProp
                    : props.reloadProp
                      ? [props.reloadProp]
                      : undefined;
                  reloadWithMergedParams(data, only);
                }
              }}
            >
              {preset.label}
            </button>
          );
        }}
      </For>
    </div>
  );
}

export function QueryParamDateRangePicker(props: {
  startQueryParam: string;
  endQueryParam: string;
  reloadProp?: string | string[] | false;
  presets?: DateRangePreset[];
  /** Preset label to apply on mount if no URL params are set (e.g. "Last 7 days") */
  defaultPreset?: string;
  timezone?: string;
  /** When set with reloadProp=false, use this full data (e.g. to preserve array params like locations[]) with mergeStrategy replace. */
  getReloadData?: (range: { start: string; end: string }) => Record<string, unknown>;
  /** Idle-state button label when no range is selected (default "Select dates"). */
  placeholder?: string;
  maxRangeMonths?: number;
}) {
  const page = usePage();
  const presets = () => props.presets ?? DEFAULT_PRESETS;

  // Get default range from preset if specified
  const getDefaultRange = () => {
    if (!props.defaultPreset) return { start: undefined, end: undefined };
    const preset = presets().find((p) => p.label === props.defaultPreset);
    if (!preset) return { start: undefined, end: undefined };
    return preset.getRange();
  };

  const urlStart = () => getSearchParams(page).get(props.startQueryParam);
  const urlEnd = () => getSearchParams(page).get(props.endQueryParam);

  // Optimistic local state so the picker updates immediately on selection
  const [optimisticStart, setOptimisticStart] = createSignal<string | undefined>(undefined);
  const [optimisticEnd, setOptimisticEnd] = createSignal<string | undefined>(undefined);

  // Clear optimistic state whenever URL params change.
  // Handles: server response arriving, clientReload (sync), and external navigation (back/forward).
  createEffect(
    on(
      [urlStart, urlEnd],
      () => {
        setOptimisticStart(undefined);
        setOptimisticEnd(undefined);
      },
      { defer: true },
    ),
  );

  // Use optimistic values first, then URL params, then default preset
  const startValue = () => optimisticStart() ?? urlStart() ?? getDefaultRange().start;
  const endValue = () => optimisticEnd() ?? urlEnd() ?? getDefaultRange().end;

  return (
    <DateRangePickerWithPresets
      startValue={startValue()}
      endValue={endValue()}
      presets={presets()}
      timezone={props.timezone}
      placeholder={props.placeholder}
      maxRangeMonths={props.maxRangeMonths}
      onChange={(range) => {
        // Update display immediately
        setOptimisticStart(range.start);
        setOptimisticEnd(range.end);

        const data = {
          [props.startQueryParam]: range.start,
          [props.endQueryParam]: range.end,
        };

        if (props.reloadProp === false) {
          if (props.getReloadData) {
            clientReload({
              data: props.getReloadData(range) as Record<string, string | string[] | undefined>,
              mergeStrategy: "replace",
            });
          } else {
            clientReload({ data });
          }
        } else {
          const only = Array.isArray(props.reloadProp)
            ? props.reloadProp
            : props.reloadProp
              ? [props.reloadProp]
              : undefined;
          reloadWithMergedParams(data, only);
        }
      }}
    />
  );
}
